/**
 * The SynAssistant button (bottom right) and its drawer. It sits below the
 * dashboard and report editors, so it is out of the way there, as
 * AgencyAnalytics keeps AgencyAI out of its editors.
 */

import { useEffect, useState, type ReactNode } from 'react';

import { apiCall } from '../api.ts';
import { AssistantDrawer } from './AssistantDrawer.tsx';

const MANAGE = 'reports.document.manage';

export function AssistantLauncher(props: {
  organisationId: string;
  permissions: string[];
  client: { id: string; name: string } | null;
}): ReactNode {
  const [open, setOpen] = useState(false);
  const [firstName, setFirstName] = useState('');
  const [conversationId, setConversationId] = useState<string | null>(null);
  // An inbox notice links to #/assistant/<conversation>: open it in the drawer.
  useEffect(() => {
    const onHash = () => {
      const match = /^#\/assistant\/([0-9a-f-]{36})$/.exec(window.location.hash);
      if (!match) return;
      setConversationId(match[1] ?? null);
      setOpen(true);
      window.history.replaceState({}, '', '#/clients');
      window.dispatchEvent(new HashChangeEvent('hashchange'));
    };
    onHash();
    window.addEventListener('hashchange', onHash);
    return () => window.removeEventListener('hashchange', onHash);
  }, []);
  useEffect(() => {
    if (!open || firstName) return;
    apiCall<{
      user?: { first_name?: string | null; display_name?: string | null };
      first_name?: string | null;
    }>('/me')
      .then((me) => setFirstName(me.user?.first_name ?? me.first_name ?? ''))
      .catch(() => undefined);
  }, [open, firstName]);
  return (
    <>
      {!open && (
        <button type="button" className="as-launch" onClick={() => setOpen(true)}>
          <span className="as-mark" aria-hidden="true">
            ✦
          </span>
          SynAssistant
        </button>
      )}
      {open && (
        <AssistantDrawer
          organisationId={props.organisationId}
          firstName={firstName}
          client={props.client}
          canManage={props.permissions.includes(MANAGE)}
          conversationId={conversationId}
          onClose={() => {
            setOpen(false);
            setConversationId(null);
          }}
        />
      )}
    </>
  );
}
